import { useState } from 'react';

import { MissionDescriptionSkin, SectionStatus, ValidityConditionType } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

import { TradfiBoostBanner } from '../../../banner/tradfi-boost';
import { RegularMission } from '../../../types';
import { SectionDescriptionTooltipButton } from '../../misc';
import { getIsTradingVolumeMission } from '../../utils';
import DesktopMilestoneItem from './desktop-milestone-item';
import { DisabledList } from './disabled-list';
import { MobileMilestoneItem } from './mobile-milestone-item';
import { MultipleMissionCard } from './multiple-mission-wrapper';
import { useMultipleMissionsData } from './use-multiple-missions-data';

interface Props {
  missions: RegularMission[];
  validityConditions: ValidityConditionType[];
  sectionStatus: SectionStatus;
  sectionDescription?: MissionDescriptionSkin;
}

export const MultipleMissions = ({ missions, validityConditions, sectionStatus, sectionDescription }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    missionStatus,
    filteredMissions,
    totalMissionCount,
    completedMissionCount,
    alwaysShowMissions,
    milestones,
    claimedMissions,
    expiredMissions,
    rejectedMissions,
    totalReward,
    showExpiredTime,
    daysUntilExpiration,
    sectionTitle,
    tooltipText,
  } = useMultipleMissionsData({
    missions,
    validityConditions,
    sectionStatus,
    sectionDescription,
  });

  const renderedMilestones = isExpanded ? milestones : alwaysShowMissions;
  const hasTradingVolumeMission = missions.some((mission) =>
    getIsTradingVolumeMission(mission.spec?.completionConditions),
  );

  return (
    <>
      {/* tablet+ */}
      <div className={css({ display: { base: 'none', tablet: 'block' } })}>
        <MultipleMissionCard
          sectionTitle={sectionTitle}
          showExpiredTime={showExpiredTime}
          daysUntilExpiration={daysUntilExpiration}
        >
          <DesktopMilestoneItem missions={missions} missionTooltipText={tooltipText} />
          {hasTradingVolumeMission && <TradfiBoostBanner css={{ mt: 8, mx: 32 }} />}
        </MultipleMissionCard>
      </div>
      {/* base (mobile) */}
      <div className={css({ display: { base: 'block', tablet: 'none' } })}>
        <MultipleMissionCard
          sectionTitle={sectionTitle}
          showExpiredTime={showExpiredTime}
          daysUntilExpiration={daysUntilExpiration}
        >
          <div className={hstack({ gap: 4, justify: 'space-between' })}>
            <Formatted.Number
              value={totalReward}
              precision={0}
              toFixed
              css={{ textStyle: 'Title/L/Monospace', color: 'content.accent.promotion' }}
            />
            {tooltipText && <SectionDescriptionTooltipButton text={tooltipText} />}
          </div>
          <div className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2', mt: 4 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Total_rewards' />
          </div>
          <div
            className={hstack({
              gap: 10,
              mt: 8,
              py: 16,
              justify: 'space-between',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderBottomColor: 'border.default.level-1',
            })}
          >
            <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Completed' />
            </div>
            <span className={css({ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' })}>
              {completedMissionCount} / {totalMissionCount}
            </span>
          </div>

          <div>
            {missionStatus &&
              renderedMilestones.map((mission, index) => {
                if (!mission?.spec) return null;
                return (
                  <MobileMilestoneItem
                    key={`assigned-mission-${index}`}
                    completionConditions={mission.spec.completionConditions}
                    mission={mission}
                    isFirst={index === 0}
                    isLast={index === milestones.length - 1}
                    timeline={milestones.length > 1}
                  />
                );
              })}
            {!missionStatus &&
              filteredMissions.map((mission, index) => {
                if (!mission?.spec) return null;
                return (
                  <MobileMilestoneItem
                    key={`unassigned-mission-${index}`}
                    completionConditions={mission.spec.completionConditions}
                    mission={mission}
                    isFirst={index === 0}
                    isLast={index === filteredMissions.length - 1}
                    timeline
                  />
                );
              })}
          </div>

          {isExpanded && (
            <>
              <DisabledList title={<FormattedMessage id='Reward_hub.r250213.Claimed' />} missions={claimedMissions} />
              <DisabledList title={<FormattedMessage id='Reward_hub.r250213.Expired' />} missions={expiredMissions} />
              <DisabledList title={<FormattedMessage id='Kyc.r250311.Rejected' />} missions={rejectedMissions} />
            </>
          )}

          <TextButton
            icon={
              <ChevronDownIcon
                size={16}
                className={css({
                  color: 'content.accent.info',
                  transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                })}
              />
            }
            label={<FormattedMessage id='Reward_hub.r251216.View_all_milestones' />}
            onClick={() => setIsExpanded((prev) => !prev)}
            css={{ justifyContent: 'center', mt: 8, mb: 4 }}
          />
          {hasTradingVolumeMission && <TradfiBoostBanner css={{ mt: 8 }} />}
        </MultipleMissionCard>
      </div>
    </>
  );
};
